import { useMemo } from 'react';

import { useListRiskScoresQuery } from '../api';
import { buildRiskScoreRequest } from '../logic/riskScoreRequest';

export interface RiskEngineAvailability {
  /**
   * Confirmed-positive: the business unit has a published scoring day
   * (`hasData === true`). `false` while loading, on error, and on confirmed absence.
   */
  readonly isAvailable: boolean;
  /**
   * Whether navigation should surface the Risk Engine entry. Optimistic: `true` unless
   * availability is *confirmed absent* (`hasData === false`). While the probe is loading or
   * has errored, availability is unknown, so the entry stays shown — this avoids a load-time
   * layout shift and prevents a transient network error from permanently suppressing a flagged
   * feature. The route guard and the page's own empty/error states handle the uncertain case.
   */
  readonly shouldShow: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Checks whether the active business unit has a published Risk Engine scoring day.
 * A one-row request is enough because `hasData` is business-unit-wide.
 */
export function useRiskEngineAvailability(
  businessUnitId: string | undefined,
  enabled: boolean,
): RiskEngineAvailability {
  const input = useMemo(
    () => buildRiskScoreRequest({ businessUnitId, maxResults: 1 }),
    [businessUnitId],
  );
  const { data, isLoading, isError } = useListRiskScoresQuery(input, {
    enabled: enabled && !!businessUnitId,
  });

  const hasData = data?.hasData;

  return {
    isAvailable: enabled && hasData === true,
    // Hide only on a confirmed negative; loading/error (hasData === undefined) stays shown.
    shouldShow: enabled && hasData !== false,
    isLoading: enabled && isLoading,
    isError,
  };
}
